import { Dialog } from '@angular/cdk/dialog';
import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  computed,
  DestroyRef,
  inject,
  output,
  Signal,
  TemplateRef,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NgIcon } from '@ng-icons/core';
import { TranslateModule } from '@ngx-translate/core';
import { LucideAngularModule } from 'lucide-angular';
import { firstValueFrom, from, interval, of, Subscription, switchMap } from 'rxjs';
import { OndemandUtilsService } from 'src/shared/services/ondemand.utils/ondemand.utils.service';
import { ChatStatus } from 'src/shared/stores/chat/chat-data.store';
import { ChatFacade } from 'src/shared/stores/chat/chat-facade.store';
import { PolicyStore } from 'src/shared/stores/policy/policy.store';
import { UserStore } from 'src/shared/stores/user.store';
import { ChatTypeSelectionDialogComponent } from 'src/shared/components/joii-chat/vet-care/chat-type-selection-dialog/chat-type-selection-dialog.component';
import { NoPaddingDialogConfig } from 'src/shared/config/dialog.config';
import { AuthenticationService } from 'src/shared/services/auth/auth_service';
import { entities } from 'src/shared/services/client/client';
import { GrowthbookService } from 'src/shared/services/growthbook/growthbook.service';
import { TimezoneService } from 'src/shared/services/timezone/timezone.service';
import {
  CustomAttachmentListContext,
  CustomTemplatesService,
  StreamAutocompleteTextareaModule,
  StreamChatModule,
  StreamI18nService,
  ThemeService,
} from 'stream-chat-angular';
import { consultationexternal } from 'src/shared/services/client/client';
import { MixpanelService } from 'src/shared/services/mix-panel.service/mix-panel.service';
import { BookingDataService } from 'src/shared/services/booking.data.service/booking.data.service';
import { ChatDataService } from 'src/shared/services/chat.data.service/chat.data.service';
import { AiStatusComponent } from '../ai-status/ai-status.component';
import { ChatHeaderComponent } from '../chat-header/chat-header.component';
import { ChatPetSelectionComponent } from '../chat-pet-selection/chat-pet-selection.component';
import { ChatWindowPaymentComponent } from '../chat-window-payment/chat-window-payment.component';
import { DisruptedCallMessageComponent } from '../disrupted-call-message/disrupted-call-message.component';
import { EndedCallMessageComponent } from '../ended-call-message/ended-call-message.component';
import { GenericChatButtonAttachmentComponent } from '../generic-chat-button-attachment/generic-chat-button-attachment.component';
import { IncomingCallMessageComponent } from '../incoming-call-message/incoming-call-message.component';
import { InprogressCallMessageComponent } from '../inprogress-call-message/inprogress-call-message.component';
import { MissedCallMessageComponent } from '../missed-call-message/missed-call-message.component';
import { ProductMessageComponent } from '../product-message/product-message.component';

@Component({
  selector: 'app-chat-window',
  imports: [
    StreamChatModule,
    TranslateModule,
    StreamAutocompleteTextareaModule,
    LucideAngularModule,
    ChatHeaderComponent,
    CommonModule,
    IncomingCallMessageComponent,
    EndedCallMessageComponent,
    InprogressCallMessageComponent,
    MissedCallMessageComponent,
    DisruptedCallMessageComponent,
    AiStatusComponent,
    ProductMessageComponent,
    NgIcon,
    GenericChatButtonAttachmentComponent,
    ChatWindowPaymentComponent,
    ChatPetSelectionComponent,
  ],
  templateUrl: './chat-window.component.html',
  styleUrl: './chat-window.component.scss',
  encapsulation: ViewEncapsulation.None,
})
export class ChatWindowComponent implements AfterViewInit {
  chatStore = inject(ChatFacade);
  userStore = inject(UserStore);
  ondemandUtils = inject(OndemandUtilsService);
  themeService = inject(ThemeService);
  growthBook = inject(GrowthbookService);
  dialog = inject(Dialog);
  authService = inject(AuthenticationService);
  customTemplatesService = inject(CustomTemplatesService);
  streamI18nService = inject(StreamI18nService);
  timezoneService = inject(TimezoneService);
  chatDataService = inject(ChatDataService);
  mixpanelService = inject(MixpanelService);
  bookingDataService = inject(BookingDataService);
  destroyRef = inject(DestroyRef);
  policyStore = inject(PolicyStore, { optional: true });
  chatAvailability = this.chatStore.availability.chatAvailabilityResponse;
  createChatDisabled = this.chatStore.availability.disabled;

  private isInsurer = computed(() => !!this.policyStore?.tenant()?.insurer);

  eligiblePets = computed(() => {
    const allPets = this.userStore.pets();
    if (this.isInsurer()) {
      return allPets.filter((pet) => pet.policy_active);
    }
    return allPets;
  });
  @ViewChild('customAttachmentPreviews')
  customAttachmentPreviewsTemplate!: TemplateRef<CustomAttachmentListContext>;
  currentChatId = computed(() => {
    return ((this.chatStore.conn.activeChannel()?.data!['ExtraData'] as any)?.['chat_doc_id'] as string) ?? '';
  });
  dismissed = output<void>();
  recommendedProductRepeatSubscription: Subscription = new Subscription();
  recommendedProductsRepeatInterval = interval(10000);
  recommendedProductsRepeatLimitCount: number = 4;
  recommendedProductsRepeatCount: number = 0;
  recommendedProductsResponse!: consultationexternal.InstantRecommendationResponse;
  recommendedProductsLastChatID!: string;
  showProductsOverlay: boolean = false;
  chatStatus = ChatStatus;
  currentChat = computed(() => {
    const activeChannel = this.chatStore.conn.activeChannel();
    return this.chatStore.data.chats().find((c) => c.channel_id === activeChannel?.id);
  });

  /**
   * Constructor
   * Initializes Stream I18n Service and sets the theme to light.
   */
  constructor() {
    this.streamI18nService.setTranslation();
    this.themeService.theme$.next('light');
  }
  ngAfterViewInit(): void {
    this.customTemplatesService.customAttachmentListTemplate$.next(this.customAttachmentPreviewsTemplate);
    this.chatDataService.recommendedProductsObservable
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((chatData) => {
        this.recommendedProductsRepeatCount = 0;
        this.recommendedProductsLastChatID = chatData;
        this.getRecommendedProducts();
        this.startRecommendedProductPoll();
      });
  }

  startRecommendedProductPoll(): void {
    this.recommendedProductsRepeatInterval.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.getRecommendedProducts();
    });
  }

  getRecommendedProducts() {
    if (
      !this.recommendedProductsResponse &&
      this.recommendedProductsRepeatCount <= this.recommendedProductsRepeatLimitCount
    ) {
      const productRecommendationRequest = {
        ConsultationDocID: '',
        AppointmentDocID: '',
        ChatDocID: '',
        UTMSource: 'joii-app-consult',
        UTMMedium: 'instant-recommendation',
        UTMCampaign: 'chat',
      };
      if (this.recommendedProductsLastChatID) {
        productRecommendationRequest.ChatDocID = this.recommendedProductsLastChatID;
      }
      this.bookingDataService
        .GetRecommendedProductsByConsultation(productRecommendationRequest)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (result) => {
            this.handleRecommendedProductLimitBump();
            this.recommendedProductsResponse = result;
            this.showProductsOverlay = true;
            this.mixpanelService.track('product.instant_recommendations', {
              page_name: 'chat_product_instant_recommendations',
              chat_id: this.recommendedProductsLastChatID,
            });
          },
          error: () => {
            this.handleRecommendedProductLimitBump();
          },
        });
    }
  }

  handleRecommendedProductLimitBump() {
    this.recommendedProductsRepeatCount++;
    if (this.recommendedProductsRepeatCount >= this.recommendedProductsRepeatLimitCount) {
      this.recommendedProductRepeatSubscription.unsubscribe();
    }
  }
  async onCreateNewChatClick(
    llmChatEnabled = this.growthBook.featureValue<boolean>('llm_pre_chat_triage_enabled', false)
  ) {
    if (this.chatStore.availability.disabled()) {
      return;
    }
    if (this.chatStore.data.incompleteChats().length > 0) {
      return;
    }
    if (this.chatStore.data.awaitingPayment() !== undefined) {
      this.chatStore.openStripePaymentLink(this.chatStore.data.awaitingPayment());
      return;
    }
    if (!this.chatStore.availability.available()) {
      return;
    }
    if (this.chatStore.data.creating()) {
      return;
    }
    if (this.authService.isStaff()) {
      this.openChatSelectionDialog();
      return;
    }

    if (this.eligiblePets().length > 1) {
      this.chatStore.ui.setActiveTab('pet-selection');
      return;
    }

    const status = llmChatEnabled ? ChatStatus.llmChatTriage : ChatStatus.curious;

    try {
      await this.createNewChat(status);
      this.trackConsultationTypeSelect('chat');
    } catch (error) {
      console.error('Error creating chat:', error);
    }
  }

  async onPetConfirmed(pet: entities.PetResponse) {
    const pendingStatus = this.chatStore.consumePendingChatStatus();
    const llmChatEnabled = this.growthBook.featureValue<boolean>('llm_pre_chat_triage_enabled', false);
    const status = pendingStatus ?? (llmChatEnabled ? ChatStatus.llmChatTriage : ChatStatus.curious);

    this.userStore.setActivePet(pet);

    try {
      await this.createNewChat(status);
      this.trackConsultationTypeSelect('chat');
    } catch (error) {
      console.error('Error creating chat:', error);
      this.chatStore.ui.setActiveTab('channels');
    }
  }

  onPetSelectionBack() {
    this.chatStore.ui.setActiveTab('channels');
  }

  onPetSelectionClose() {
    this.chatStore.ui.closeWidget();
  }
  /**
   * Creates a new chat with the active pet.
   */
  async createNewChat(status: ChatStatus) {
    const params = {
      pet_doc_id: this.userStore.activePet()?.doc_id || '',
      timezone: this.timezoneService.getClientTimezone(),
      status,
    };
    const chat = await firstValueFrom(this.chatStore.createNewChat(params));
    this.chatStore.ui.setActiveTab('chat');
    return chat;
  }

  closeProductOverlay(): void {
    this.showProductsOverlay = false;
  }

  goToBasket(basketURL: string) {
    if (basketURL) {
      this.mixpanelService.track('product.checkout', {
        page_name: 'chat_product_instant_recommendations',
        chat_id: this.recommendedProductsLastChatID,
      });
      window.open(basketURL, '_blank');
    }
  }

  goToCart(cartURL: string | undefined) {
    if (cartURL) {
      this.mixpanelService.track('product.basket', {
        page_name: 'chat_product_instant_recommendations',
        chat_id: this.recommendedProductsLastChatID,
      });
      window.open(cartURL, '_blank');
    }
  }

  gotoActiveChat(event: Event) {
    if ((event.target as HTMLElement).closest('stream-channel-preview')) {
      this.chatStore.ui.setActiveTab('chat');
    }
  }

  trackConsultationTypeSelect(type = 'chat') {
    this.mixpanelService.track('consultation.type.select', { 'consultation.button.type': type });
  }

  openChatSelectionDialog() {
    const label = computed(() =>
      this.ondemandUtils.getChatAvailabilityLabel(
        this.chatStore.availability.chatAvailabilityResponse(),
        this.chatStore.data.incompleteChats(),
        this.chatStore.data.awaitingPayment()
      )
    );

    const availabilityStatus = computed(() =>
      this.ondemandUtils.getChatAvailabilityStatus(
        this.chatStore.availability.chatAvailabilityResponse(),
        this.chatStore.data.incompleteChats(),
        this.chatStore.data.awaitingPayment()
      )
    );

    const reference = this.dialog.open<
      ChatStatus | undefined,
      { chat_availability_label: Signal<string>; chat_availability_status: Signal<string> },
      ChatTypeSelectionDialogComponent
    >(ChatTypeSelectionDialogComponent, {
      ...NoPaddingDialogConfig,
      data: {
        chat_availability_label: label,
        chat_availability_status: availabilityStatus,
      },
    });
    reference.closed
      .pipe(
        switchMap((data: ChatStatus | undefined) => {
          if (data) {
            if (this.eligiblePets().length > 1) {
              this.chatStore.setPendingChatStatus(data);
              this.chatStore.ui.setActiveTab('pet-selection');
              return of(null);
            }
            return from(this.createNewChat(data));
          }
          return of(null);
        })
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (chatCreatedResponse) => {
          if (!chatCreatedResponse) return;
          const chat = this.chatStore.data.chats().find((c) => c.doc_id === chatCreatedResponse?.doc_id);
          if (chat && chat.payment_link && chat.status === ChatStatus.paymentRequired) {
            this.chatStore.openStripePaymentLink(chat);
          }
        },
      });
  }
}
